
/**
 * LA GEOCERCA, MEDIDA EN EL TELÉFONO CUANDO NO HAY A QUIÉN PREGUNTARLE.
 *
 * La regla de que hay que estar en el sitio para empezar vive en la base
 * (`fn_marcar_llegada`, código GEO01) y ahí se queda: es la única que no se
 * puede burlar editando un teléfono. Pero sin señal la base no contesta, así
 * que el técnico marcaba, se iba, y se enteraba del rechazo horas después
 * —cuando ya no podía caminar los metros que le faltaban—.
 *
 * Esto NO mueve la regla de sitio: la repite aquí para avisar a tiempo. La
 * base la vuelve a medir al subir y su palabra es la que vale. Si algún día
 * las dos cuentas no coinciden, manda la de allá.
 *
 * EL GPS NO NECESITA SEÑAL, y eso es lo que hace posible todo esto: son
 * satélites y el teléfono solo escucha. En modo avión con la ubicación
 * encendida la posición llega igual; lo que falta sin datos es la ayuda para
 * fijar rápido (puede tardar 30-60 s en frío en vez de 3) y la ubicación por
 * wifi y torres. Por eso la exactitud se respeta tal cual viene.
 *
 * LA CUENTA ES LA MISMA DE LA BASE, copiada a propósito al pie de la letra:
 *   · tolerancia = lo que el GPS diga de su propio error, hasta 100 m
 *   · se rechaza si la distancia pasa del radio MÁS esa tolerancia
 *   · y si el GPS viene peor de 200 m, NO SE JUZGA A NADIE: la base no
 *     bloquea con una medida en la que ella misma no cree, y aquí tampoco.
 */

/** Hasta aquí se le cree al GPS. Peor que esto, no se juzga. */
const EXACTITUD_INSERVIBLE_M = 200

/** Lo que se le perdona por imprecisión, como mucho. */
const TOLERANCIA_MAXIMA_M = 100

/** El sitio de un trabajo, tal como lo guarda «Mi trabajo» para la calle. */
export type SitioDelTrabajo = {
  orden_trabajo_id: string
  latitud: number | null
  longitud: number | null
  radio_m: number | null
  fecha_inicio_programada?: string | null
  fecha_fin_programada?: string | null
}

/** Metros entre dos puntos. La misma cuenta que hace `fn_distancia_km`. */
export function distanciaEnMetros(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const rad = Math.PI / 180
  const a =
    Math.sin(((lat2 - lat1) * rad) / 2) ** 2 +
    Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(((lon2 - lon1) * rad) / 2) ** 2

  return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export type VeredictoDeGeocerca =
  /** Está en el sitio, o no hay con qué juzgarlo. Que siga. */
  | { dentro: true; distancia: number | null }
  /** Está lejos, y se sabe con qué seguridad. */
  | { dentro: false; distancia: number; radio: number; mensaje: string }

/**
 * Mide el punto contra la geocerca del trabajo, con lo que haya guardado.
 *
 * Devuelve «dentro» siempre que no pueda demostrar lo contrario: sin sitio
 * guardado, sin radio, o con un GPS que no merece crédito. Un aviso que se
 * dispara por una duda es peor que no avisar: enseña a la gente a ignorarlo.
 */
export function medirContraLaGeocerca(
  sitio: SitioDelTrabajo | undefined,
  punto: { latitud: number; longitud: number; exactitud: number | null },
  /** Qué está intentando hacer, para que el aviso hable de eso y no de «empezar». */
  gesto: 'empezar' | 'terminar' | 'reportar el avance' = 'empezar',
): VeredictoDeGeocerca {
  if (!sitio || sitio.latitud === null || sitio.longitud === null || !sitio.radio_m) {
    return { dentro: true, distancia: null }
  }

  // El GPS avisando de que no sabe dónde está. No se juzga con eso.
  if ((punto.exactitud ?? 0) > EXACTITUD_INSERVIBLE_M) {
    return { dentro: true, distancia: null }
  }

  const distancia = distanciaEnMetros(
    sitio.latitud,
    sitio.longitud,
    punto.latitud,
    punto.longitud,
  )
  const tolerancia = Math.min(punto.exactitud ?? 0, TOLERANCIA_MAXIMA_M)

  if (distancia <= sitio.radio_m + tolerancia) {
    return { dentro: true, distancia }
  }

  return {
    dentro: false,
    distancia,
    radio: sitio.radio_m,
    // Se dice igual que lo dice la base, para que el técnico lea lo mismo
    // tenga señal o no la tenga.
    mensaje:
      `Debes estar en el sitio para ${gesto}. Estás a ${Math.round(distancia)} m ` +
      `y la geocerca es de ${sitio.radio_m} m. Si de verdad estás aquí y el GPS ` +
      `falla, pide autorización.`,
  }
}

/**
 * El sitio de un trabajo dentro de la lista guardada de «Mi trabajo».
 *
 * No pide nada a la red: la lista ya quedó en el teléfono la última vez que
 * hubo señal, y de ahí salen el punto y el radio. Si no está, devuelve
 * undefined y quien llame no juzga.
 */
export function sitioGuardadoDe(
  trabajos: SitioDelTrabajo[] | null,
  ordenTrabajoId: string,
): SitioDelTrabajo | undefined {
  return (trabajos ?? []).find((t) => t.orden_trabajo_id === ordenTrabajoId)
}


/**
 * SI HOY ES UNO DE LOS DÍAS DE ESE TRABAJO.
 *
 * La misma regla que `fn_es_su_dia` en la base (0216), repetida aquí por lo
 * mismo que la geocerca: para poder decírselo al técnico en la obra y no dos
 * horas después. La base la vuelve a mirar al subir y su palabra es la que
 * vale.
 *
 * Se compara por día y en hora de Cartago: llegar a las 6:40 a un trabajo de
 * las 7:00 es lo normal, y un trabajo de varios días vale cualquiera de
 * ellos. Sin fecha programada no se juzga.
 */
export function esSuDia(
  sitio: SitioDelTrabajo | undefined,
  cuando: Date = new Date(),
): { suDia: true } | { suDia: false; mensaje: string } {
  const inicio = sitio?.fecha_inicio_programada
  if (!inicio) return { suDia: true }

  // El día del calendario en Cartago, como lo ve la base.
  const enCartago = (d: Date) =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Bogota',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d)

  const hoy = enCartago(cuando)
  const desde = enCartago(new Date(inicio))
  const hasta = enCartago(new Date(sitio.fecha_fin_programada ?? inicio))

  if (hoy >= desde && hoy <= hasta) return { suDia: true }

  const [a, m, d] = desde.split('-')
  return {
    suDia: false,
    mensaje:
      `Este trabajo está programado para el ${d}/${m}/${a}. Hoy no es su día: ` +
      `pide a la oficina que lo reprograme.`,
  }
}
